import type { CustomFieldDefinitionId } from "@repo/app-types/entities/customField";
import type { DateRange } from "@repo/app-types/entities/dateRange";
import {
  findNodeWithPath,
  isPlaybookSectionNode,
  type PlaybookId,
  type PlaybookNode,
  type PlaybookNodeId,
} from "@repo/app-types/entities/playbook";
import { renderPlural } from "@repo/app-types/helpers/utilities";
import {
  MultiSelect,
  type MultiSelectOption,
} from "@repo/ui/Select/multiSelect";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { Duration } from "luxon";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useReportDataStreamQuery } from "../../api/entities/performanceReport.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useCustomFieldDefinitionSelectProps } from "../../components/customFieldDefinitionSelect.js";
import {
  buildTeamPerformancePageFilters,
  dateRangeToISORange,
} from "../filters/buildFilterQueryArgs.js";
import { DateRangeFilterItem } from "../filters/dateRangeFilterItem.js";
import { useFilters } from "../filters/filters.js";
import { useCompanyDefaultDateRange } from "../filters/useCompanyDefaultDateRange.js";
import type { ReportRenderProps } from "./baseReport.js";

const FALLBACK_DEFAULT_PERIOD: DateRange = {
  length: Duration.fromObject({ days: 7 }),
  end: undefined,
};

export interface ComplianceReportProps {
  playbookId: PlaybookId;
  children: (props: ReportRenderProps) => ReactNode;
}

export const ComplianceReport: FC<ComplianceReportProps> = (props) => {
  const { playbookId } = props;

  const now = useNow();
  const endOfYesterday = useMemo(
    () => now.minus({ days: 1 }).endOf("day"),
    [now],
  );
  const [
    {
      teammateGroupIds,
      outcome,
      customFields,
      playbookConditions,
      totalRecordingDuration,
    },
    renderFilters,
  ] = useFilters(
    [
      "teammateGroupIds",
      "outcome",
      "customFields",
      "playbookConditions",
      "totalRecordingDuration",
    ],
    { playbookId },
  );

  // Use a distinct period filter, since the existing one defaults to the
  // company's default date range.
  // TODO: Clean up once we revisit other pages and how these filters should interact if at all.
  const companyDefaultPeriod = useCompanyDefaultDateRange();
  const [customPeriod, setPeriod] = useState<DateRange | undefined>(undefined);
  const period: DateRange =
    customPeriod ?? companyDefaultPeriod ?? FALLBACK_DEFAULT_PERIOD;

  const [playbookNodeIds, setPlaybookNodeIds] = useState<
    readonly PlaybookNodeId[] | undefined
  >(undefined);
  const [customFieldDefinitionId, setCustomFieldDefinitionId] = useState<
    CustomFieldDefinitionId | undefined
  >(undefined);

  const prevPlaybookIdRef = useRef(playbookId);
  useEffect(() => {
    if (playbookId !== prevPlaybookIdRef.current) {
      setPlaybookNodeIds(undefined);
    }

    prevPlaybookIdRef.current = playbookId;
  }, [playbookId]);

  const { data: playbook } = usePlaybookByIdQuery(playbookId);
  const playbookNodeOptions = useMemo(() => {
    if (!playbook?.assessment_tree) {
      return [];
    }

    function* traverse(
      nodes: readonly PlaybookNode[],
      depth: number,
    ): Iterable<MultiSelectOption> {
      for (const node of nodes) {
        if (isPlaybookSectionNode(node)) {
          yield { id: node.id, label: node.name, indent: depth };
          yield* traverse(node.nodes, depth + 1);
        } else {
          yield { id: node.id, label: node.name, indent: depth };
        }
      }
    }

    return [
      {
        id: "nodes",
        options: [...traverse(playbook.assessment_tree.nodes, 0)],
      },
    ];
  }, [playbook?.assessment_tree]);

  const playbookNodesLabel = useMemo(() => {
    if (!playbook?.assessment_tree || !playbookNodeIds?.length) {
      return undefined;
    }

    const [firstNodeId] = playbookNodeIds;
    if (playbookNodeIds.length === 1 && firstNodeId !== undefined) {
      const node = findNodeWithPath(
        playbook.assessment_tree,
        firstNodeId,
      )?.node;
      return node?.name ?? "Unknown criterion";
    }

    return renderPlural(playbookNodeIds.length, {
      one: "1 criterion",
      other: (count) => `${count.toLocaleString()} criteria`,
    });
  }, [playbook?.assessment_tree, playbookNodeIds]);

  const { customFieldLabel, customFieldOptions } =
    useCustomFieldDefinitionSelectProps(customFieldDefinitionId);

  const { data, isFetching, refetch } = useReportDataStreamQuery({
    type: "compliance",
    periodLengthDays: Math.round(period.length.as("days")),
    // Default to the last full day (i.e. yesterday) to avoid showing a weird trend due to partial data
    periodEnd: period.end ?? endOfYesterday,
    // Use the user's time zone
    timeZone: now.zone,
    playbookId,
    playbookNodeIds,
    customFieldDefinitionId,
    filters: buildTeamPerformancePageFilters({
      playbookId,
      teammateGroupIds: teammateGroupIds ?? null,
      outcome,
      customFields,
      playbookConditions,
      totalRecordingDuration,
      signalId: null,
      signalMetadataFields: [],
      selectedDateRange: dateRangeToISORange(now, {
        length: period.length,
        end: period.end ?? endOfYesterday,
      }),
      selectedDateRangeTimeZone: now.zoneName,
    }),
  });

  return props.children({
    title: "Weekly compliance report",
    isScopedToPlaybook: true,
    settings: (
      <>
        <MultiSelect
          label={playbookNodesLabel}
          placeholder="Select criteria to show"
          sections={playbookNodeOptions}
          value={playbookNodeIds ?? []}
          onChange={setPlaybookNodeIds}
        />

        <MultiSelect
          label={customFieldLabel}
          placeholder="Break down by custom field"
          sections={customFieldOptions}
          value={customFieldDefinitionId ? [customFieldDefinitionId] : []}
          onChange={(ids) => {
            setCustomFieldDefinitionId(ids.at(-1));
          }}
        />
      </>
    ),
    renderFilterBar: (options) =>
      renderFilters({
        isLoading: isFetching,
        trailingItems: (
          <DateRangeFilterItem
            hasBasisPicker={false}
            value={period}
            onChange={setPeriod}
          />
        ),
        rightActions: options?.rightActions,
      }),
    onRefresh: refetch,
    state: data?.state,
  });
};
